import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
  IconButton,
  Grid2,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Add,
  CalendarToday,
  Folder,
  Search,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  ChevronLeft,
  ChevronRight,
  Grading,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import CardAgendamento from "../../../componentes/easyReport/cardAgendamento";
import GroupsReport from "../../../componentes/easyReport/groupsReport";
import { styles } from "./styles";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import AlertModal from "../../../componentes/alertModal";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import AgendamentoRelatorios from "../../../componentes/easyReport/historicoAgendamento";
import ReportFilters from "../../../componentes/easyReport/reportFilters";
import dayjs = require("dayjs");

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface IAgendamento {
  id: string;
  titulo: string;
  perfil: string[];
  items: string[];
  dataInicial: string;
  dataFinal: string;
}
export interface AgendamentoGroup {
  id: string;
  titulo: string;
  relatorios: IAgendamento[];
}
export default function Agendamento() {
  const [tabValue, setTabValue] = useState<number>(0);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [agendamentos, setAgendamentos] = useState<IAgendamento[]>([]);
  const [grupos, setGrupos] = useState<AgendamentoGroup[]>([]);

  const [openNewGroupModal, setOpenNewGroupModal] = useState<boolean>(false);
  const [newGroupTitle, setNewGroupTitle] = useState<string>("");
  const [selectedAgendamentos, setSelectedAgendamentos] = useState<
    IAgendamento[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [agendamentoId, setAgendamentoId] = useState<string | null>();
  const [filtroDataInicial, setFiltroDataInicial] =
    useState<dayjs.Dayjs | null>(null);
  const [filtroDataFinal, setFiltroDataFinal] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [filtroGeradoEm, setFiltroGeradoEm] = useState<dayjs.Dayjs | null>(
    null,
  );

  const [page, setPage] = useState(1);
  const itemsPerPage = useMemo(() => (tabValue === 0 ? 6 : 3), [tabValue]);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoadingPage(true);
        const res = await api.get("/easyReport/v2/findManySchedules");
        setAgendamentos(res.data);
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
      } finally {
        setLoadingPage(false);
      }
    };
    const handleApiGetGroupMaps = async () => {
      try {
        setLoadingPage(true);
        const response = await api.get("/easyReport/v2/findManyGroup");

        if (response.data) {
          setGrupos(response.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingPage(false);
      }
    };
    fetchAppointments();
    handleApiGetGroupMaps();
  }, []);
  useEffect(() => {
    setPage(1);
  }, [tabValue, searchValue]);

  const filteredAppointments = useMemo(() => {
    return agendamentos.filter((row) => {
      const search = searchValue.toLowerCase();
      const matchesSearch =
        row.titulo.toLowerCase().includes(search) ||
        row.items.some((item) => item.toLowerCase().includes(search));

      return matchesSearch;
    });
  }, [searchValue, agendamentos]);

  const filteredGroups = useMemo(() => {
    return grupos.filter((row) => {
      const search = searchValue.toLowerCase();
      const matchesSearch = row.titulo.toLowerCase().includes(search);

      return matchesSearch;
    });
  }, [searchValue, grupos]);

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await api.post("/easyReport/v2/deleteSchedule", {
        id: selectedId,
      });
      setAgendamentos((prev) => prev.filter((r) => r.id !== selectedId));
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/agendamento",
        "Agendamento excluído com sucesso!",
        "success",
        3000,
      );
    } catch (error) {
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/agendamento",
        (error as any)?.response?.data?.error || "Erro ao deletar agendamento",
        "error",
        3000,
      );
    }
  };
  const handleCreateGroup = async () => {
    try {
      const response = await api.post("/easyReport/v2/createGroup", {
        titulo: newGroupTitle,
        relatorioIds: selectedAgendamentos.map((a) => a.id),
      });
      if (response.data.status === "Error") {
        notifyOnPage(
          window.location.pathname,
          "Erro ao criar grupo de agendamento",
          "error",
          3000,
        );
      } else {
        if (response.data) {
          setGrupos((prev) => [...prev, response.data]);
        }
      }
    } catch (error: any) {
      console.error("Erro ao criar grupo", error);
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao criar grupo de agendamento",
        "error",
        3000,
      );
    } finally {
      setOpenNewGroupModal(false);
      setNewGroupTitle("");
      setSelectedAgendamentos([]);
    }
  };
  const paginatedAppointments = useMemo(() => {
    const lastIndex = page * itemsPerPage;
    const firstIndex = lastIndex - itemsPerPage;
    return filteredAppointments.slice(firstIndex, lastIndex);
  }, [filteredAppointments, page, itemsPerPage]);

  const paginatedGroups = useMemo(() => {
    const lastIndex = page * itemsPerPage;
    const firstIndex = lastIndex - itemsPerPage;
    return filteredGroups.slice(firstIndex, lastIndex);
  }, [filteredGroups, page, itemsPerPage]);

  const totalPages = Math.ceil(
    (tabValue === 0 ? filteredAppointments.length : filteredGroups.length) /
      itemsPerPage,
  );
  return (
    <Box sx={styles.page}>
      <Typography variant="h6">Agendamentos</Typography>
      <Box sx={styles.container}>
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tabValue}
            onChange={(event: React.SyntheticEvent, newValue: number) => {
              setTabValue(newValue);
            }}
          >
            <Tab
              icon={<CalendarToday fontSize="small" />}
              label={"Agendamentos"}
              iconPosition="start"
              value={0}
              sx={styles.tab}
            />
            <Tab
              icon={<Folder fontSize="small" />}
              label={"Grupos"}
              iconPosition="start"
              value={1}
              sx={styles.tab}
            />
            <Tab
              icon={<Grading fontSize="small" />}
              label={"Histórico"}
              iconPosition="start"
              value={2}
              sx={styles.tab}
            />
          </Tabs>
          <Paper sx={styles.paper} elevation={0}>
            <Box
              sx={{
                ...styles.headerBox,
                justifyContent: tabValue === 2 ? "flex-start" : "space-between",
              }}
            >
              <TextField
                size="small"
                value={searchValue}
                placeholder="Pesquisar"
                sx={styles.searchInput}
                onChange={(e) => setSearchValue(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {tabValue === 2 && (
                <Autocomplete
                  size="small"
                  id="checkboxes-reports"
                  options={agendamentos}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.titulo}
                  value={
                    agendamentos.find((a) => a?.id === agendamentoId) || null
                  }
                  onChange={(_, value) => setAgendamentoId(value?.id || null)}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.titulo}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={styles.textfield}
                      placeholder="Selecione o agendamento"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          startAdornment: (
                            <CalendarToday
                              style={{
                                width: 22,
                                height: 22,
                                fill: theme.palette.text.secondary,
                              }}
                            />
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
              {tabValue !== 2 && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    if (tabValue !== 0) {
                      setOpenNewGroupModal(true);
                    }
                    if (tabValue === 0) {
                      navigate("/easyReportV2/agendamento/novoAgendamento");
                    }
                  }}
                >
                  {tabValue === 0 ? "Novo Agendamento" : "Novo grupo"}
                </Button>
              )}
            </Box>
            {tabValue === 2 && (
              <ReportFilters
                filtroDataFinal={filtroDataFinal}
                setFiltroDataFinal={setFiltroDataFinal}
                filtroDataInicial={filtroDataInicial}
                setFiltroDataInicial={setFiltroDataInicial}
                filtroGeradoEm={filtroGeradoEm}
                setFiltroGeradoEm={setFiltroGeradoEm}
              />
            )}
          </Paper>
        </Box>

        {loadingPage ? (
          Array.from({ length: 6 }).map((_, index: number) => (
            <Skeleton key={index} variant="rectangular" sx={styles.skeleton} />
          ))
        ) : (
          <>
            <Grid2
              container
              spacing={2}
              sx={{
                justifyContent: { xs: "center", sm: "start" },
                width: "100%",
                height: "100%",
              }}
            >
              {tabValue == 0 &&
                Array.isArray(paginatedAppointments) &&
                paginatedAppointments.length > 0 &&
                paginatedAppointments.map((card, index) => (
                  <Grid2 key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <CardAgendamento
                      {...card}
                      inGroup={false}
                      setDeleteModal={setDeleteModal}
                      setSelectedId={setSelectedId}
                    />
                  </Grid2>
                ))}
              {tabValue === 1 && (
                <Box
                  sx={{
                    width: "100%",
                  }}
                >
                  <GroupsReport
                    grupos={paginatedGroups}
                    agendamentos={agendamentos}
                    setGrupos={setGrupos}
                  />
                </Box>
              )}
              {tabValue === 2 && (
                <Box
                  sx={{
                    width: "100%",
                  }}
                >
                  <AgendamentoRelatorios
                    id={agendamentoId || null}
                    searchValue={searchValue}
                    filtroDataFinal={filtroDataFinal}
                    filtroDataInicial={filtroDataInicial}
                    filtroGeradoEm={filtroGeradoEm}
                  />
                </Box>
              )}
              {tabValue !== 2 && (
                <Grid2
                  key={"pagination-agendamento"}
                  size={12}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    paddingY: 2,
                  }}
                >
                  <Stack sx={styles.stackPagination}>
                    <Typography variant="body2" color="text.secondary">
                      Página {page} de {totalPages || 1}
                    </Typography>

                    <Box sx={styles.iconBtns}>
                      <IconButton
                        disabled={page === 1}
                        onClick={() => setPage((prev) => prev - 1)}
                        size="small"
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        disabled={page >= totalPages}
                        onClick={() => setPage((prev) => prev + 1)}
                        size="small"
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  </Stack>
                </Grid2>
              )}
            </Grid2>
          </>
        )}
        <Dialog
          open={openNewGroupModal}
          onClose={() => {
            setOpenNewGroupModal(false);
            setNewGroupTitle("");
            setSelectedAgendamentos([]);
          }}
        >
          <DialogTitle color="primary">
            Criar novo grupo
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenNewGroupModal(false);
                setNewGroupTitle("");
                setSelectedAgendamentos([]);
              }}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={styles.dialogContent}>
            <Typography>Título</Typography>
            <TextField
              placeholder="Título do grupo"
              size="small"
              sx={styles.textfield}
              autoComplete="off"
              value={newGroupTitle}
              onChange={(e) => setNewGroupTitle(e.target.value)}
            />
            <Typography>Adicionar relatórios</Typography>
            <Autocomplete
              multiple
              size="small"
              id="checkboxes-reports"
              options={agendamentos}
              disableCloseOnSelect
              getOptionLabel={(option: IAgendamento) => option.titulo}
              value={selectedAgendamentos}
              onChange={(_, value) => setSelectedAgendamentos(value)}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.titulo}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={styles.textfield}
                  placeholder="Selecione os relatórios"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              sx={styles.buttonCancelDialog}
              onClick={() => {
                setOpenNewGroupModal(false);
                setNewGroupTitle("");
                setSelectedAgendamentos([]);
              }}
            >
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleCreateGroup}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <AlertModal
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        severity="delete"
        singleButton={false}
        handleButton={handleDelete}
        textContent={
          "Se prosseguir com a operação, o agendamento será movido para a lixeira e excluído permanentemente após 30 dias."
        }
        handleCancel={() => {
          setDeleteModal(false);
        }}
      />
    </Box>
  );
}
